import * as THREE from 'three';
import type { ReferencePlane } from '../../../../../reference/ReferencePlane';
import type { SplineProjectionResult, WorldCoordinate } from '../../../model/ProjectionResult';
import type { ResolvedSplineReferenceSource } from '../SketchReferenceSourceResolver';
import type { SupportProjectionPreprocessor } from './SupportProjectionPreprocessor';
import {
	PROJECTION_EPSILON,
	toWorldCoordinate,
} from './SupportProjectionPreprocessor';

/**
 * 普通样条源投影预处理器。
 * 说明：
 * 1. 样条转换实体引用使用控制点和采样点共同表达目标曲线；
 * 2. 控制点用于后续 PropertyManager 与 rebuild 保持编辑语义；
 * 3. 采样点用于立即构建稳定的显示几何，避免引入异步 Worker 依赖。
 */
export class SplineSupportProjectionPreprocessor
	implements SupportProjectionPreprocessor<ResolvedSplineReferenceSource, SplineProjectionResult>
{
	/**
	 * 执行普通样条投影。
	 *
	 * @param input 投影输入。
	 * @returns 成功时返回 spline，退化时返回 null。
	 */
	public projectToTargetPlane(input: {
		source: ResolvedSplineReferenceSource;
		targetReferencePlane: ReferencePlane;
	}): SplineProjectionResult | null {
		const controlPointWorlds = projectWorldCoordinatesToPlane(
			input.source.controlPointWorlds,
			input.targetReferencePlane,
		);
		const curvePointWorlds = projectWorldCoordinatesToPlane(
			input.source.curvePointWorlds,
			input.targetReferencePlane,
		);
		if (!hasUsableCurveExtent(controlPointWorlds) || !hasUsableCurveExtent(curvePointWorlds)) {
			return null;
		}

		// 显示采样与精确极点必须在同一次投影中进入目标平面，否则预览和参数求解会表达两条不同曲线。
		return {
			sourceDescriptor: input.source.sourceDescriptor,
			sourceKind: 'spline',
			projectionResultKind: 'spline',
			diagnostics: [],
			geometryPayload: {
				controlPointWorlds,
				curvePointWorlds,
				curveMetadata: input.source.curveMetadata,
				...(input.source.curveEvaluationDefinition
					? {
						curveEvaluationDefinition: this.projectSplineEvaluationDefinitionToPlane(
							input.source.curveEvaluationDefinition,
							input.targetReferencePlane,
						),
					}
					: {}),
			},
		};
	}

	/**
	 * 将内核 B 样条定义中的世界极点投影到目标参考面。
	 *
	 * 权重、节点、次数和参数范围均属于参数曲线定义，正交投影只改变极点坐标。
	 *
	 * @param definition 来源权威 B 样条定义。
	 * @param targetReferencePlane 目标草图参考面。
	 * @returns 与目标草图共面的独立 B 样条定义。
	 */
	private projectSplineEvaluationDefinitionToPlane(
		definition: SketchBSplineEvaluationDefinition,
		targetReferencePlane: ReferencePlane,
	): SketchBSplineEvaluationDefinition {
		return {
			...definition,
			controlPoints: definition.controlPoints.map((point) => {
				const local = targetReferencePlane.worldToLocal(
					new THREE.Vector3(point.x, point.y, point.z),
				);
				const world = targetReferencePlane.localToWorld(local);
				return {x: world.x, y: world.y, z: world.z};
			}),
			weights: [...definition.weights],
			knots: [...definition.knots],
		};
	}
}

/**
 * 将世界坐标点集合正交投影到目标参考面。
 *
 * @param pointsWorld 输入世界坐标集合。
 * @param targetReferencePlane 目标参考面。
 * @returns 投影后的世界坐标集合。
 */
export function projectWorldCoordinatesToPlane(
	pointsWorld: WorldCoordinate[],
	targetReferencePlane: ReferencePlane,
): WorldCoordinate[] {
	return pointsWorld.map((pointWorld) => {
		const localPoint = targetReferencePlane.worldToLocal(new THREE.Vector3(...pointWorld));
		return toWorldCoordinate(targetReferencePlane.localToWorld(localPoint));
	});
}

/**
 * 判断投影后的曲线是否仍有可用几何长度。
 *
 * @param pointsWorld 投影后的世界坐标集合。
 * @returns 至少存在两个不同点时返回 true。
 */
export function hasUsableCurveExtent(pointsWorld: WorldCoordinate[]): boolean {
	if (pointsWorld.length < 2) {
		return false;
	}

	const firstPoint = new THREE.Vector3(...pointsWorld[0]);
	return pointsWorld.some((pointWorld) => (
		firstPoint.distanceTo(new THREE.Vector3(...pointWorld)) > PROJECTION_EPSILON
	));
}
